
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import TermsRepository from "../../repositories/implementations/TermsRepository";
import TranslationsRepository from "../../repositories/implementations/TranslationsRepository";
import { Project } from "../../entities/Project";
import { ProjectLanguage } from "../../entities/ProjectLanguage";
import { Term } from "../../entities/Term";
import { Translation } from "../../entities/Translation";
import { getFlagCode, getLanguageName } from "../../utils/languageUtils";
import { toastError } from "../../utils/toast";

@Component
export default class SearchResultsView extends Vue {
  @Prop({ required: true }) project!: Project;

  @Prop({ required: true }) languages!: ProjectLanguage[];

  @Prop({ required: true }) query!: string;

  loading = false;

  subTab = 0;

  matchedTerms: Term[] = [];

  // termId → languageCode → Translation
  expandedData: Record<string, Record<string, Translation>> = {};

  // termId → Set of complete language codes
  completedByTerm: Map<string, Set<string>> = new Map();

  expandedTermId: string | null = null;

  termsRepo = new TermsRepository();

  translationsRepo = new TranslationsRepository();

  // Pending plural edits
  pluralPending: Record<string, Record<string, { one: string | null; other: string | null }>> = {};

  get matchedTermsByLang(): Record<string, Term[]> {
    const result: Record<string, Term[]> = {};
    this.languages.forEach((lang) => {
      result[lang.languageCode] = this.matchedTerms.filter((term) => {
        const tr = this.expandedData[term.id]?.[lang.languageCode];
        return tr !== undefined;
      });
    });
    return result;
  }

  flagCode(code: string): string {
    return getFlagCode(code);
  }

  languageName(code: string): string {
    return getLanguageName(code);
  }

  getExpandedValue(termId: string, langCode: string): Translation | undefined {
    return this.expandedData[termId]?.[langCode];
  }

  termPercent(termId: string): number {
    if (this.languages.length === 0) return 0;
    const completed = this.completedByTerm.get(termId)?.size ?? 0;
    return Math.round((completed / this.languages.length) * 100);
  }

  async created() {
    await this.runSearch();
  }

  @Watch("query")
  async onQueryChanged() {
    await this.runSearch();
  }

  async runSearch() {
    if (!this.query.trim()) return;
    this.loading = true;
    this.matchedTerms = [];
    this.expandedData = {};
    this.expandedTermId = null;

    try {
      const allTerms = await this.termsRepo.getByProject(this.project.id);
      const q = this.query.toLowerCase();

      // Match by key
      const matchedByKey = new Set(allTerms.filter((t) => t.key.toLowerCase().includes(q)).map((t) => t.id));

      // Match by translation value
      const matchedTranslations = await this.translationsRepo.searchByValueInProject(this.project.id, this.query);
      const matchedByValue = new Set(matchedTranslations.map((tr) => tr.termId));

      // Union
      const matchedIds = new Set([...matchedByKey, ...matchedByValue]);
      this.matchedTerms = allTerms.filter((t) => matchedIds.has(t.id));

      // Load all translations for matched terms to populate expanded view
      const allTranslations: Record<string, Record<string, Translation>> = {};
      const completedByTerm = new Map<string, Set<string>>();

      await Promise.all(
        this.matchedTerms.map(async (term) => {
          const translations = await this.translationsRepo.getByTermId(term.id);
          allTranslations[term.id] = {};
          const completed = new Set<string>();
          translations.forEach((tr) => {
            allTranslations[term.id][tr.languageCode] = tr;
            if (tr.isComplete(term.isPlural)) completed.add(tr.languageCode);
          });
          completedByTerm.set(term.id, completed);
        })
      );

      this.expandedData = allTranslations;
      this.completedByTerm = completedByTerm;
    } catch (e: any) {
      toastError("Search failed.");
    } finally {
      this.loading = false;
    }
  }

  toggleExpand(termId: string) {
    this.expandedTermId = this.expandedTermId === termId ? null : termId;
  }

  async saveTranslation(term: Term, langCode: string, valueOne: string, valueOther: string | null) {
    const trimmed = valueOne.trim();
    if (!trimmed) return;
    const existing = this.expandedData[term.id]?.[langCode];
    if (existing && trimmed === existing.valueOne?.trim() && valueOther === existing.valueOther) return;
    try {
      const saved = await this.translationsRepo.upsert(term.id, langCode, trimmed, valueOther);
      if (!this.expandedData[term.id]) this.$set(this.expandedData, term.id, {});
      this.$set(this.expandedData[term.id], langCode, saved);
      if (saved.isComplete(term.isPlural)) {
        const set = this.completedByTerm.get(term.id) || new Set();
        set.add(langCode);
        this.completedByTerm.set(term.id, set);
        this.completedByTerm = new Map(this.completedByTerm);
      }
      this.$emit("translations-updated");
    } catch {
      toastError("Save failed.");
    }
  }

  async savePluralOne(term: Term, langCode: string, valueOne: string) {
    if (!this.pluralPending[term.id]) this.pluralPending[term.id] = {};
    if (!this.pluralPending[term.id][langCode]) this.pluralPending[term.id][langCode] = { one: null, other: null };
    this.pluralPending[term.id][langCode].one = valueOne.trim();

    const { one } = this.pluralPending[term.id][langCode];
    const existingOther = this.expandedData[term.id]?.[langCode]?.valueOther ?? null;
    const other = this.pluralPending[term.id][langCode].other ?? existingOther;
    if (one && other !== null) {
      await this.saveTranslation(term, langCode, one, other);
      delete this.pluralPending[term.id][langCode];
    }
  }

  async savePluralOther(term: Term, langCode: string, valueOther: string) {
    if (!this.pluralPending[term.id]) this.pluralPending[term.id] = {};
    if (!this.pluralPending[term.id][langCode]) this.pluralPending[term.id][langCode] = { one: null, other: null };
    this.pluralPending[term.id][langCode].other = valueOther.trim();

    const { other } = this.pluralPending[term.id][langCode];
    const existingOne = this.expandedData[term.id]?.[langCode]?.valueOne ?? null;
    const one = this.pluralPending[term.id][langCode].one ?? existingOne;
    if (one && other !== null) {
      await this.saveTranslation(term, langCode, one, other);
      delete this.pluralPending[term.id][langCode];
    }
  }
}
